/* AR / Collections → Statements (Duncan 2026-10-15): "a view and exportable report to give to a
   client who has multi invoices (or just one) open". One card per customer with anything open in
   CE (lib/statement groups jobs by the customer code their job numbers start with); Statement
   opens that customer's printable statement in a new tab (pages/StatementPage.js).
   2026-10-19 (Duncan: "the appearance isn't matching the look and prestige that we are using with
   the rest of the system"): the Job Costs / Billing by Phase look -- tiles across the top, a card
   per customer, and a click drops down what its statement will show: the aging and every open
   invoice and credit. The statement itself is unchanged. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getInvoiceHistory } from '../../api';
import LoadingState from '../LoadingState';
import StateScreen from '../ui/StateScreen';
import { formatMoney } from '../../lib/format';
import { matchesSearch, noMatchText } from '../../lib/pageSearch';
import { AGING, agedItems, agingOf, monthName, openByCustomer, postedThrough } from '../../lib/statement';
import { todayCentral } from '../../lib/invoiceHistory';
import { JcIcon } from '../jobcost/JobCostShared';
import { Icon } from '../ui';
import KpiRow from './ArKpiRow';
import { MetricCol } from './ArCardParts';

export const statementPath = (code) => `/ar/statement/${encodeURIComponent(code)}`;

const money = (n) => (n < -0.004 ? `−${formatMoney(-n)}` : formatMoney(n));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (ymd) => {
  const p = ymd ? String(ymd).slice(0, 10).split('-').map(Number) : null;
  return p ? `${MONTHS[p[1] - 1]} ${p[2]}, ${p[0]}` : '–';
};
const cents = (n) => Math.round(n * 100) / 100;
const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
// A customer card's columns: the customer, Balance, Open, Past due, Statement, the chevron.
const CARD_COLS = 'minmax(260px, 1fr) 180px 110px 180px 128px 22px';
// The drop-down's invoice table: the statement's columns (StatementPage), and the status.
const LINE_COLS = '96px 112px 112px minmax(110px, 1fr) minmax(110px, 1fr) 138px minmax(120px, 1fr)';
const PAST_DUE = AGING.filter((b) => b.key !== 'current').map((b) => b.key);

// A page with lines: the statement.
const PageGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" />
  </svg>
);

const TONES = {
  danger: { color: 'var(--danger)', bg: 'var(--danger-soft)' },
  success: { color: 'var(--success)', bg: 'var(--success-soft)' },
  neutral: { color: 'var(--text-3)', bg: 'var(--bg-surface-2)' },
};
const Pill = ({ tone, children }) => (
  <span style={{ justifySelf: 'start', display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: TONES[tone].color, background: TONES[tone].bg }}>{children}</span>
);
// An item on the statement: a credit, past due by its age (a credit follows its invoice), or current.
const statusOf = (i) => (i.open < 0 ? <Pill tone="success">Credit</Pill>
  : i.age > 0 ? <Pill tone="danger">{i.age} day{i.age === 1 ? '' : 's'} past due</Pill>
    : <Pill tone="neutral">Current</Pill>);

/* The statement's aging buckets, as it prints them: the past-due ones in red when they hold anything. */
const AgingStrip = ({ aging }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${AGING.length}, minmax(96px, 1fr))`, gap: 8 }}>
    {AGING.map((b) => {
      const v = aging[b.key];
      const late = b.key !== 'current' && Math.abs(v) > 0.004;
      return (
        <div key={b.key} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', padding: '8px 12px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{b.label}</div>
          <div className="mono" style={{ marginTop: 4, fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: late ? 'var(--danger)' : Math.abs(v) > 0.004 ? 'var(--text-1)' : 'var(--text-4)' }}>{Math.abs(v) > 0.004 ? money(v) : '–'}</div>
        </div>
      );
    })}
  </div>
);

/* A customer's drop-down: what its statement will show -- the aging, then every open invoice and
   credit (by job when it has several), and the balance. */
const CustomerDetail = ({ c }) => {
  const items = useMemo(() => agedItems(c.items), [c.items]);
  const aging = useMemo(() => agingOf(c.items), [c.items]);
  const multiJob = c.jobs.length > 1;
  const groups = multiJob ? c.jobs.map((j) => ({ ...j, items: items.filter((i) => i.job === j.job) })) : [{ ...c.jobs[0], items }];
  const head = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' };
  const right = { textAlign: 'right' };
  return (
    <div style={{ background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-subtle)', padding: '14px 18px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 640 }}><AgingStrip aging={aging} /></div></div>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 860 }}>
            <div style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 10, padding: '9px 14px', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)', ...head }}>
              <span>Invoice</span><span>Date</span><span>Due</span><span style={right}>Amount</span><span style={right}>Applied</span><span>Status</span><span style={right}>Open</span>
            </div>
            {groups.map((g) => (
              <React.Fragment key={g.job}>
                {multiJob && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 14px 6px', borderTop: '1px solid var(--border-subtle)', fontSize: 13 }}>
                    <span className="mono" style={{ fontWeight: 700, color: 'var(--text-1)' }}>{g.job}</span>
                    <span style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                  </div>
                )}
                {g.items.map((i) => (
                  <div key={`${i.job}-${i.number}`} style={{ display: 'grid', gridTemplateColumns: LINE_COLS, gap: 10, alignItems: 'center', padding: '7px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 13.5, color: 'var(--text-2)' }}>
                    <span className="mono" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{i.number}</span>
                    <span>{fmtDay(i.date || i.paid_on)}</span>
                    <span style={{ color: 'var(--text-3)' }}>{i.due ? fmtDay(i.due) : '–'}</span>
                    <span className="mono" style={{ ...right, fontVariantNumeric: 'tabular-nums' }}>{money(i.amount)}</span>
                    <span className="mono" style={{ ...right, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{money(i.amount - i.open)}</span>
                    {statusOf(i)}
                    <span className="mono" style={{ ...right, fontWeight: 700, color: i.open > 0 && i.age > 0 ? 'var(--danger)' : 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{money(i.open)}</span>
                  </div>
                ))}
              </React.Fragment>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', fontSize: 13.5 }}>
              <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>Balance</span>
              <span style={{ color: 'var(--text-3)' }}>· {c.openCount} open invoice{c.openCount === 1 ? '' : 's'}{c.creditCount ? `, ${c.creditCount} credit${c.creditCount === 1 ? '' : 's'}` : ''}</span>
              <span style={{ flex: 1 }} />
              <span className="mono" style={{ fontWeight: 800, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{money(c.balance)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* What billing the statements include (lib/statement postedThrough): a warning while last month's
   billings aren't posted, so nobody hands a customer a statement missing them; a quiet line once in. */
export const PostedFlag = ({ posted }) => {
  if (!posted.latest) return null;
  const latest = `the latest posted invoice is ${fmtDay(posted.latest)}`;
  return posted.behind ? (
    <div role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.5,
      color: 'var(--warning)', background: 'var(--warning-soft)', border: '1px solid color-mix(in oklch, var(--warning) 30%, transparent)' }}>
      <span style={{ display: 'inline-flex', marginTop: 2 }}><Icon name="alert-triangle" size={16} /></span>
      <span><strong>{monthName(posted.lastMonth)} billings haven&rsquo;t been posted yet</strong>, so they aren&rsquo;t on any statement &mdash; {latest}.</span>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 4px', fontSize: 13, color: 'var(--text-3)' }}>{monthName(posted.lastMonth)} billings are posted &mdash; {latest}.</div>
  );
};

/* One customer: what it owes, how much is past due, and its statement. A click drops down the detail. */
const CustomerCard = ({ c, open, onToggle }) => {
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } };
  const jobsText = c.jobs.length <= 3 ? c.jobs.map((j) => `#${j.job}`).join(' · ') : `${c.jobs.slice(0, 2).map((j) => `#${j.job}`).join(' · ')} · +${c.jobs.length - 2} jobs`;
  return (
    <article style={{ ...card, overflow: 'hidden' }}>
      <div className="stv-cardhead" role="button" tabIndex={0} aria-expanded={open} onClick={onToggle} onKeyDown={onKey} style={{ cursor: 'pointer', padding: '15px 18px' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gap: 22, alignItems: 'center', minWidth: 900, gridTemplateColumns: CARD_COLS }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.jobs.map((j) => `${j.job} · ${j.name}`).join('\n')}>{c.name}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)', flex: 'none' }}>{c.code}</span>
              </div>
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{jobsText}</span>
            </div>
            <MetricCol big label="Balance" value={money(c.balance)} sub={`${c.jobs.length} job${c.jobs.length === 1 ? '' : 's'}`} />
            <MetricCol label="Open" value={String(c.openCount)} sub={c.creditCount ? `${c.creditCount} credit${c.creditCount === 1 ? '' : 's'}` : 'invoices'} />
            <MetricCol label="Past due" value={c.pastDueAmount > 0.004 ? money(c.pastDueAmount) : '–'} color={c.pastDueAmount > 0.004 ? 'var(--danger)' : 'var(--text-4)'}
              sub={c.pastDueCount > 0 ? `oldest ${c.oldestPastDue} day${c.oldestPastDue === 1 ? '' : 's'}` : 'all current'} subColor={c.pastDueCount > 0 ? 'var(--danger)' : 'var(--success)'} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="stv-btn" onClick={(e) => { e.stopPropagation(); window.open(statementPath(c.code), '_blank', 'noopener'); }} title={`Open ${c.name}'s statement to print or save as a PDF`}>
                <PageGlyph /> Statement
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--text-3)' }}>
              <span style={{ display: 'inline-flex', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={18} /></span>
            </div>
          </div>
        </div>
      </div>
      {open && <CustomerDetail c={c} />}
    </article>
  );
};

const StatementsView = ({ searchState }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // The search box here and the top bar's are one text: ARPage holds it for this view (lib/pageSearch).
  const ownSearch = useState('');
  const [q, setQ] = searchState || ownSearch;
  const [openCodes, setOpenCodes] = useState(() => new Set()); // the customers dropped down
  const toggle = (code) => setOpenCodes((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  const load = useCallback(() => {
    setError(null);
    getInvoiceHistory()
      .then((d) => setData(d))
      .catch((e) => setError(e.message || 'Could not load the open invoices'));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Past due as the statement ages it (lib/statement agingOf: a credit counts where its invoice does).
  const customers = useMemo(() => openByCustomer(data).map((c) => {
    const aging = agingOf(c.items);
    return { ...c, pastDueAmount: cents(PAST_DUE.reduce((s, k) => s + aging[k], 0)) };
  }), [data]);
  const shown = useMemo(() => {
    if (!q.trim()) return customers;
    // Every word typed, in the customer's name or code or any of its jobs (as on every page).
    return customers.filter((c) => matchesSearch(q, c.name, c.code, c.jobs.map((j) => [j.job, j.name])));
  }, [customers, q]);
  // The tiles add up what the list shows (they follow the search).
  const totals = useMemo(() => {
    const credits = shown.flatMap((c) => c.items.filter((i) => i.open < 0));
    const oldest = shown.reduce((m, c) => (c.oldestPastDue > (m ? m.oldestPastDue : 0) ? c : m), null);
    return {
      balance: cents(shown.reduce((s, c) => s + c.balance, 0)),
      pastDue: cents(shown.reduce((s, c) => s + c.pastDueAmount, 0)),
      pastDueCustomers: shown.filter((c) => c.pastDueAmount > 0.004).length,
      oldest,
      credits: credits.length,
      creditAmount: cents(credits.reduce((s, i) => s + i.open, 0)),
    };
  }, [shown]);

  const posted = postedThrough(data, todayCentral());

  if (error) return <StateScreen kind="error" title="Couldn't load the open invoices" body={error} primary={{ label: 'Try again', icon: 'refresh-cw', onClick: load }} />;
  if (!data) return <LoadingState message="Loading statements…" />;
  if (customers.length === 0) return <StateScreen kind="empty" title="Nothing open" body="No customer has an open invoice in the latest AR pull." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`
        @media (min-width: 1290px) { .stv-top { position: sticky; top: 56px; z-index: 6; } }
        .stv-search { height: 32px; width: 280px; max-width: 100%; padding: 0 12px; border-radius: 8px; font: 500 14px var(--font-sans);
          color: var(--text-1); background: var(--bg-surface); border: 1px solid var(--border-default); box-shadow: var(--shadow-xs); }
        .stv-search:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .stv-cardhead:hover { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .stv-cardhead:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
        .stv-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 32px; padding: 0 13px; border-radius: 8px;
          font: 600 13.5px var(--font-sans); color: var(--accent-text); background: var(--bg-surface);
          border: 1px solid color-mix(in oklch, var(--accent) 35%, var(--border-default)); box-shadow: var(--shadow-xs); cursor: pointer; white-space: nowrap; }
        .stv-btn:hover { background: var(--accent-soft); }
      `}</style>
      <section className="stv-top" style={{ ...card, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input type="search" className="stv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers or jobs" aria-label="Search customers or jobs" />
        <div style={{ flex: 1, minWidth: 8 }} />
        <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {shown.length} of {customers.length} customer{customers.length === 1 ? '' : 's'} · as of {fmtDay(data.as_of)}
        </span>
      </section>

      <PostedFlag posted={posted} />

      <KpiRow label="Statement totals" tiles={[
        { label: 'Open balance', value: money(totals.balance), valueSize: 20, sub: `${shown.length} customer${shown.length === 1 ? '' : 's'}`, muted: !shown.length },
        { label: 'Past due', value: money(totals.pastDue), valueSize: 20, valueColor: totals.pastDue > 0.004 ? 'var(--danger)' : undefined, sub: `${totals.pastDueCustomers} customer${totals.pastDueCustomers === 1 ? '' : 's'} past due`, muted: !(totals.pastDue > 0.004) },
        { label: 'Oldest past due', value: totals.oldest ? `${totals.oldest.oldestPastDue} days` : '–', valueSize: 20, valueColor: totals.oldest ? 'var(--danger)' : undefined, sub: totals.oldest ? totals.oldest.name : 'nothing past due', muted: !totals.oldest },
        { label: 'Credits open', value: String(totals.credits), valueSize: 20, sub: totals.credits ? `${money(totals.creditAmount)} not applied yet` : 'none open', muted: !totals.credits },
      ]} />

      {shown.length === 0 ? (
        <StateScreen kind="empty" {...noMatchText(q)} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {shown.map((c) => <CustomerCard key={c.code} c={c} open={openCodes.has(c.code)} onToggle={() => toggle(c.code)} />)}
        </div>
      )}
    </div>
  );
};

export default StatementsView;
